import React from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Trash2, AlertTriangle } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * Information about cascading deletes
 */
interface CascadeInfo {
  /** Label describing what will be deleted */
  label: string;
  /** Number of items that will be deleted */
  count: number;
}

/**
 * Props for DeleteConfirmModal
 */
interface DeleteConfirmModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when delete is confirmed */
  onConfirm: () => void;
  /** Title of the modal */
  title?: string;
  /** Name of the item being deleted */
  itemName: string;
  /** Type of item (e.g., "aspiration", "goal", "list") */
  itemType?: string;
  /** Additional description or warning */
  description?: string;
  /** Information about related items that will also be deleted */
  cascadeInfo?: CascadeInfo[];
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
  /** Variant affecting visual style */
  variant?: 'default' | 'danger';
}

/**
 * Reusable delete confirmation modal
 * Provides consistent delete confirmation UI across the application
 */
export function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  itemName,
  itemType = 'item',
  description,
  cascadeInfo,
  isDeleting = false,
  variant = 'default',
}: DeleteConfirmModalProps) {
  const hasCascadeEffects = cascadeInfo && cascadeInfo.length > 0 && cascadeInfo.some(c => c.count > 0);
  const effectiveVariant = hasCascadeEffects ? 'danger' : variant;

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={title || `Delete ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`}
    >
      <div className="space-y-4">
        {/* Icon and message */}
        <div className="flex items-start gap-4">
          <div className={cn(
            'p-2 rounded-full flex-shrink-0',
            effectiveVariant === 'danger' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-orange-100 dark:bg-orange-900/30'
          )}>
            {effectiveVariant === 'danger' ? (
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            ) : (
              <Trash2 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold break-words">"{itemName}"</span>?
            </p>
          </div>
        </div>

        {/* Description */}
        {description && (
          <p className="text-sm text-muted-foreground pl-12">
            {description}
          </p>
        )}

        {/* Cascade effects warning */}
        {hasCascadeEffects && (
          <div className={cn(
            'rounded-lg p-3 ml-12',
            'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
          )}>
            <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
              This will also delete:
            </p>
            <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
              {cascadeInfo!.filter(c => c.count > 0).map((item) => (
                <li key={item.label} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  <span>
                    {item.count} {item.label}{item.count !== 1 ? 's' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warning about irreversibility */}
        <p className="text-xs text-muted-foreground pl-12">
          This action cannot be undone.
        </p>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Hook to manage delete confirmation state
 */
export function useDeleteConfirm<T>() {
  const [itemToDelete, setItemToDelete] = React.useState<T | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const openDeleteConfirm = React.useCallback((item: T) => {
    setItemToDelete(item);
  }, []);

  const closeDeleteConfirm = React.useCallback(() => {
    if (!isDeleting) {
      setItemToDelete(null);
    }
  }, [isDeleting]);

  const handleDelete = React.useCallback(async (deleteFn: (item: T) => Promise<void>) => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    try {
      await deleteFn(itemToDelete);
      setItemToDelete(null);
    } catch (error) {
      console.error('Delete failed:', error);
      // Keep modal open on error so user can retry or cancel
    } finally {
      setIsDeleting(false);
    }
  }, [itemToDelete]);

  return {
    itemToDelete,
    isDeleting,
    isOpen: itemToDelete !== null,
    openDeleteConfirm,
    closeDeleteConfirm,
    handleDelete,
  };
}
